import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CalendarClock, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { AppointmentPicker } from "@/components/AppointmentPicker";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManageBooking() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [booking, setBooking] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [error, setError] = useState("");
  const [refreshSignal, setRefreshSignal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`, { params: { token } });
      setBooking(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "This booking link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  }, [bookingId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const reschedule = async () => {
    if (!selectedSlot) {
      toast.error("Choose a new appointment time first");
      return;
    }
    setAction("reschedule");
    try {
      const response = await axios.post(`${API}/bookings/${bookingId}/reschedule`, { token, appointment_start: selectedSlot });
      setBooking(response.data.booking);
      setSelectedSlot("");
      setRefreshSignal((current) => current + 1);
      toast.success("Appointment rescheduled", { description: response.data.booking.appointment_label });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Appointment could not be rescheduled");
      setRefreshSignal((current) => current + 1);
    } finally {
      setAction("");
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this consultation and release the appointment time?")) return;
    setAction("cancel");
    try {
      const response = await axios.post(`${API}/bookings/${bookingId}/cancel`, { token });
      setBooking(response.data.booking);
      setRefreshSignal((current) => current + 1);
      toast.success("Appointment cancelled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Appointment could not be cancelled");
    } finally {
      setAction("");
    }
  };

  if (loading) {
    return <main className="grid min-h-[70vh] place-items-center bg-brand-sand" data-testid="manage-booking-loading"><Loader2 className="h-8 w-8 animate-spin text-brand-copper" /></main>;
  }

  if (error) {
    return (
      <main className="bg-brand-sand py-24" data-testid="manage-booking-error-page">
        <div className="section-shell max-w-3xl">
          <div className="rounded-lg bg-white p-8 shadow-editorial">
            <XCircle className="h-10 w-10 text-red-700" />
            <h1 className="mt-6 font-serif text-3xl font-bold">Booking link unavailable</h1>
            <p className="mt-4 text-brand-navy/65" data-testid="manage-booking-error">{error}</p>
            <Button asChild className="mt-8 rounded-full bg-brand-navy text-white">
              <Link to="/contact" data-testid="manage-booking-contact-link">Schedule a New Consultation</Link>
            </Button>
          </div>
        </div>
      </main>
    );
  }

  const cancelled = booking.status === "cancelled";

  return (
    <main className="bg-brand-sand py-16 sm:py-24" data-testid="manage-booking-page">
      <div className="section-shell grid max-w-6xl gap-8 lg:grid-cols-[0.85fr_1.15fr]">
        <section className="rounded-lg bg-brand-navy p-7 text-white shadow-editorial sm:p-9">
          <p className="overline text-brand-copper">Manage Booking</p>
          <h1 className="mt-4 font-serif text-4xl font-black leading-tight">{cancelled ? "Appointment Cancelled" : "Your Consultation"}</h1>
          <div className="mt-8 rounded-lg bg-white/[0.06] p-5">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/48">Current appointment</p>
            <p className="mt-3 font-serif text-2xl font-bold" data-testid="manage-current-appointment">{booking.appointment_label}</p>
            <p className="mt-3 text-sm text-white/68">{booking.service_label}</p>
            <p className="mt-4 inline-flex rounded-full bg-brand-cream px-3 py-1 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-navy" data-testid="manage-booking-status">{booking.status}</p>
          </div>
          {cancelled && (
            <Button asChild className="mt-8 rounded-full bg-brand-copper text-brand-navy hover:bg-brand-copper/90">
              <Link to="/contact" data-testid="cancelled-booking-new-appointment-link">Schedule a New Consultation</Link>
            </Button>
          )}
        </section>
        {!cancelled && (
          <section className="rounded-lg bg-white p-5 shadow-editorial sm:p-8" data-testid="manage-booking-actions">
            <div className="mb-6 flex items-start gap-4">
              <CalendarClock className="h-8 w-8 text-brand-copper" />
              <div>
                <h2 className="font-serif text-3xl font-bold">Choose a new time</h2>
                <p className="mt-2 text-sm leading-7 text-brand-navy/65">Available times update immediately. Your old time is released only after the new time is reserved.</p>
              </div>
            </div>
            <AppointmentPicker value={selectedSlot} onChange={setSelectedSlot} refreshSignal={refreshSignal} />
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <Button onClick={reschedule} disabled={action !== ""} data-testid="reschedule-booking-button" className="h-13 rounded-full bg-brand-navy text-white hover:bg-brand-ink">
                {action === "reschedule" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Confirm New Time
              </Button>
              <Button onClick={cancel} disabled={action !== ""} variant="outline" data-testid="cancel-booking-button" className="h-13 rounded-full border-red-200 text-red-700 hover:bg-red-50">
                {action === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Cancel Appointment
              </Button>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
